import { FC, useState, useEffect, useRef } from 'react';

import anime from 'animejs/lib/anime.es.js';

import { Achievement } from '@/types';

import styles from './Achievements.module.css';
import {
  animations,
  attachAnimations,
  interactiveStyle,
  TriggerEvent,
  createFloatingAnimation,
} from '../../utils/interactions';
import Decorations from '../Decorations/Decorations';
import Icon from '../Icon/Icon';
import ScrollPrompt from '../ScrollPrompt/ScrollPrompt';

// Define accessible color variants that match the site theme and meet WCAG 2.1 AA contrast requirements
const accessibleColors = {
  healthcare: {
    base: '#52796F', // Seafoam - from theme
    text: '#FFFFFF',
    accent: 'rgba(82, 121, 111, 0.15)',
  },
  enterprise: {
    base: '#84A98C', // Mint green - from theme
    text: '#FFFFFF',
    accent: 'rgba(132, 169, 140, 0.15)',
  },
  mobile: {
    base: '#354F52', // Deep sage - from theme
    text: '#FFFFFF',
    accent: 'rgba(53, 79, 82, 0.15)',
  },
};

const achievementsList: Achievement[] = [
  {
    text: 'Inventor on patent for first cardiac monitoring patch',
    delay: 200,
    icon: 'mdi:heart-pulse',
    color: accessibleColors.healthcare.base,
    progress: 100,
    subtitle: 'Healthcare Innovation',
    category: 'healthcare',
  },
  {
    text: 'Architected scalable solutions for Fortune 500 companies',
    delay: 400,
    icon: 'mdi:server-network',
    color: accessibleColors.enterprise.base,
    progress: 100,
    subtitle: 'Enterprise Architecture',
    category: 'enterprise',
  },
  {
    text: 'Led development for home lighting application with 100k+ installs',
    delay: 600,
    icon: 'mdi:home-lightning-bolt',
    color: accessibleColors.mobile.base,
    progress: 100,
    subtitle: 'Mobile Development',
    category: 'mobile',
  },
];

const Achievements: FC = () => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const trophyIconRef = useRef<HTMLDivElement>(null);
  const achievementIconsRef = useRef<HTMLDivElement[]>([]);
  const achievementCardsRef = useRef<HTMLButtonElement[]>([]);

  useEffect(() => {
    // Setup floating animation for trophy
    const floatingCleanup = createFloatingAnimation(trophyIconRef.current);

    // Interactive animations
    const triggers: TriggerEvent[] = ['hover', 'touch'];
    const interactiveCleanup = attachAnimations([
      {
        element: trophyIconRef.current,
        animation: animations.gentleBounce(trophyIconRef.current!),
        triggers,
      },
      ...achievementIconsRef.current.map(iconWrapper => ({
        element: iconWrapper,
        animation: animations.rotate360(iconWrapper),
        triggers: triggers,
      })),
    ]);

    return () => {
      floatingCleanup();
      interactiveCleanup();
    };
  }, []);

  const handleCardClick = (index: number) => {
    // If we're collapsing the current card
    if (expandedCard === index) {
      const card = achievementCardsRef.current[index];
      anime({
        ...animations.fadeOut(card),
        scale: [1, 0.98],
        complete: () => {
          setExpandedCard(null);
          // Fade back in
          anime({
            ...animations.fadeIn(card),
            scale: [0.98, 1],
            duration: 400,
          });
        },
      });
    } else {
      // If we're expanding a new card
      if (expandedCard !== null) {
        // Fade out current expanded card
        const currentCard = achievementCardsRef.current[expandedCard];
        anime({
          ...animations.fadeOut(currentCard),
          scale: [1, 0.98],
          complete: () => {
            // Fade in new card
            const newCard = achievementCardsRef.current[index];
            setExpandedCard(index);
            anime({
              ...animations.fadeIn(newCard),
              scale: [0.98, 1],
              duration: 400,
            });
          },
        });
      } else {
        // Just expand the new card
        const card = achievementCardsRef.current[index];
        setExpandedCard(index);
        anime({
          ...animations.fadeIn(card),
          scale: [0.98, 1],
          duration: 400,
        });
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent, index: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick(index);
    }
  };

  return (
    <section id="achievements" className={styles.achievements} aria-labelledby="achievements-title">
      <Decorations
        leftIcon={{
          icon: 'mdi:android',
          color: 'var(--mint-green)',
        }}
        rightIcon={{
          icon: 'mdi:medal',
          color: 'var(--mint-green)',
        }}
      />

      <h2 id="achievements-title" className={styles.title}>
        <div ref={trophyIconRef} style={interactiveStyle}>
          <Icon
            icon="mdi:trophy"
            className={styles.titleIcon}
            size="lg"
            aria-hidden={true}
            aria-label="Trophy Icon"
          />
        </div>
        Featured Work
      </h2>

      <ul className={styles.list} aria-label="Key achievements">
        {achievementsList.map((achievement, index) => {
          const colors = accessibleColors[achievement.category];

          return (
            <li key={achievement.text}>
              <button
                ref={el => {
                  if (el) achievementCardsRef.current[index] = el;
                }}
                className={`${styles.item} ${expandedCard === index ? styles.expanded : ''}`}
                style={
                  {
                    animationDelay: `${achievement.delay}ms`,
                    '--achievement-color': colors.base,
                    '--achievement-text': colors.text,
                    '--achievement-accent': colors.accent,
                  } as React.CSSProperties
                }
                onClick={() => handleCardClick(index)}
                onKeyDown={e => handleKeyPress(e, index)}
                aria-expanded={expandedCard === index}
                aria-controls={`achievement-content-${index}`}
              >
                <div className={styles.cardHeader}>
                  <div
                    ref={el => {
                      if (el) achievementIconsRef.current[index] = el;
                    }}
                    className={styles.iconWrapper}
                    style={interactiveStyle}
                    aria-hidden="true"
                  >
                    <Icon
                      icon={achievement.icon}
                      size="md"
                      className={styles.achievementIcon}
                      aria-hidden={true}
                      aria-label={`${achievement.subtitle} Icon`}
                    />
                  </div>
                  <div
                    className={styles.progressWrapper}
                    role="progressbar"
                    aria-valuenow={achievement.progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${achievement.subtitle} completion status`}
                  >
                    <div className={styles.progressBar}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${achievement.progress}%` }}
                      />
                    </div>
                    <span className={styles.progressText}>{achievement.progress}% Complete</span>
                  </div>
                </div>
                <div id={`achievement-content-${index}`} className={styles.cardContent}>
                  <h3 className={styles.achievementSubtitle}>{achievement.subtitle}</h3>
                  <p className={styles.achievementText}>{achievement.text}</p>
                  <div className={styles.cardRipple} aria-hidden="true" />
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      <div className={styles.scrollPromptWrapper}>
        <ScrollPrompt
          targetId="contact"
          label="Contact Me"
          className={styles.scrollPrompt}
          showIcon={true}
        />
      </div>
    </section>
  );
};

export default Achievements;
